import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  FormHelperText,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Stack,
  Box,
} from "@mui/material";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";
import AppSwitch from "components/Shared/AppSwitch";
import SignaturePadComponent from "utils/SignaturePadComponent";
import { useAuth } from "hooks/useAuth";
import { useDOUploadFile } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { getDateTimeAuto } from "utils/dateFormats";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";
import {
  useGenerateOTPCodeMutation,
  useGenerateSecurityCodeMutation,
  useValidateCodeMutation,
} from "store/services/shipmentApi";
import { useAddDeliveryMutation } from "store/services/deliveryApi";
import FonepayQR from "components/Payment/FonepayQR";
import { io } from "socket.io-client";
import PaymentSuccessAnimation from "./PaymentSuccessAnimation";
import { CheckCircle } from "@mui/icons-material";
import DriverNCHLQRPayment from "./DriverNCHLQRPayment";
import nchlLogo from "../../../../../assets/nchlLogo.png"
import fonepayLogo from "../../../../../assets/fonepayLogo.png"
import { useGetActivePaymentProvidersQuery } from "store/services/paymentIntegrationApi";


const timestamp = new Date()
  .toISOString()
  .replace(/[-:]/g, "")
  .replace("T", "_")
  .replace(/\..+/, "");

const INITIAL_STATE = {
  shipmentId: [],
  status: "Completed",
  attachments: [],
  documentUrl: "",
  signature: "",
  isActive: true,
  account: "",
  entity: "",
  receiveInBehalf: false,
  receiveBy: "",
  receiverPhone: "",
  deliveryLocation: { lat: null, lon: null },
  securityCode: "",
  securityAttachments: [],
};

const DeliveryFormDriver = ({
  isBusy = false,
  deliveryDetail = null,
  onSubmit = () => { },
  initialState = null,
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
  const isDriver = loggedInUser?.typeRefSource === "Driver";
  const { handleUploadFiles } = useDOUploadFile();
  const signaturePadRef = useRef(null);
  const [signatureError, setSignatureError] = useState(false);
  const nchlQRRef = React.useRef(null);
  const [isSecurityCodeDisabled, setIsSecurityCodeDisabled] = useState(false);
  const [isOTPCodeDisabled, setIsOTPCodeDisabled] = useState(false);
  const [securityCodeTimer, setSecurityCodeTimer] = useState(0);
  const [otpCodeTimer, setOtpCodeTimer] = useState(0);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);



  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [generateOTPCode, { isLoading: loadingGenerateOTPCode }] =
    useGenerateOTPCodeMutation();
  const [generateSecurityCode, { isLoading: loadingGenerateSecurityCode }] =
    useGenerateSecurityCodeMutation();
  const [validateCode, { isLoading: loadingValidateCode }] =
    useValidateCodeMutation();
  const [addDelivery, { isLoading: isAddingDelivery }] = useAddDeliveryMutation();

  const fonepayRef = useRef();


  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
    getValues,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      actualDeliveryTime: getDateTimeAuto(),
      entity: viewEntity?.entity || "",
      account: viewEntity?.entity?.account || "",
      deliveryLocation: { lat: null, lon: null },
      ...(initialState && {
        shipmentId: initialState.shipmentId
          ? initialState.shipmentId.map((s) => ({
            _id: s._id,
            shipmentId: s.shipmentId,
            receiver: s.receiver || `receiver_${s._id}`,
            receiverDetails: s.receiverDetails || { name: "Unknown" },
            deliverySecurity: s.deliverySecurity,
            customerDetails: s.customerDetails || null,
            vendorDetails: s.vendorDetails || null,
            remainingShipmentAmount: s.remainingShipmentAmount || 0,
          }))
          : [],
        receiver: initialState.shipmentId?.[0]?.receiverDetails
          ? {
            _id: initialState.shipmentId[0].receiver || `receiver_${initialState.shipmentId[0]._id}`,
            name: initialState.shipmentId[0].receiverDetails?.name || "Unknown",
          }
          : "",
      }),
    },
  });


  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchReceiveInBehalf = useWatch({ control, name: "receiveInBehalf" });
  const _watchReceiver = useWatch({ control, name: "receiver" });

  const { data: providersData, isLoading: loadingProviders } = useGetActivePaymentProvidersQuery();

  const isFonepayActive = providersData?.hasFonepay || false;
  const isNCHLActive = providersData?.hasNCHL || false;

  const handleClickFonepay = () => {
    if (isFonepayActive) {
      handleSubmit(() => {
        fonepayRef?.current?.handleGenerateQR();
      })();
    }
  };



  const sendOutShipments = React.useMemo(() => {
    let shipments = [];

    if (deliveryDetail) {
      const shipmentIds = deliveryDetail?.shipmentId
        ? Array.isArray(deliveryDetail.shipmentId)
          ? deliveryDetail.shipmentId
          : [deliveryDetail.shipmentId]
        : [];
      shipments = shipmentIds.map((id) => ({
        _id: id._id || id,
        shipmentId: id.shipmentId || `Unknown (${id._id || id})`,
        IsReturning: id.IsReturning || false,
        status: id.status || "Unknown",
        receiver: id.receiver || `receiver_${id._id || id}`,
        receiverDetails: id.receiverDetails || { name: "Unknown" },
        deliverySecurity: id.deliverySecurity || "Normal",
        customerDetails: id.customerDetails || null,
        vendorDetails: id.vendorDetails || null,
        remainingShipmentAmount: id.remainingShipmentAmount || 0,
      }));
    } else if (initialState?.shipmentId?.length) {
      shipments = initialState.shipmentId.map((s) => ({
        _id: s._id,
        shipmentId: s.shipmentId,
        receiver: s.receiver || `receiver_${s._id}`,
        receiverDetails: s.receiverDetails || { name: "Unknown" },
        deliverySecurity: s.deliverySecurity,
        status: "SendOut",
        isConsolidated: false,
        isOnHold: false,
        customerDetails: s.customerDetails || null,
        vendorDetails: s.vendorDetails || null,
        remainingShipmentAmount: s.remainingShipmentAmount || 0,
      }));
    }

    return shipments.filter(
      (shipment) => !_watchReceiver || shipment.receiver === _watchReceiver?._id
    );
  }, [deliveryDetail, initialState?.shipmentId, _watchReceiver?._id]);

  const receivers = React.useMemo(() => {
    const receiverMap = new Map(
      sendOutShipments
        .filter((shipment) => shipment.receiver)
        .map((shipment) => [
          shipment.receiver,
          {
            _id: shipment.receiver,
            name: shipment.receiverDetails?.name || "Unknown",
          },
        ])
    );

    return Array.from(receiverMap.values());
  }, [sendOutShipments]);

  const selectedShipment =
    _watchShipmentId?.length > 0 ? _watchShipmentId[0] : null;
  const deliverySecurity = selectedShipment?.deliverySecurity || "Normal";

  const convertDataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };


  const handleOnSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const signatureDataUrl = signaturePadRef?.current?.getSignature();
      if (!signatureDataUrl) {
        setSignatureError(true);
        return;
      }
      setSignatureError(false);

      const signatureFile = convertDataUrlToFile(
        signatureDataUrl,
        `delivery_signature_${timestamp}.png`
      );

      let securityDetails = { type: deliverySecurity };
      if (["Security Code", "OTP"].includes(deliverySecurity)) {
        if (!data.securityCode) {
          toast.error(`${deliverySecurity} is required.`);
          return;
        }
        try {
          await validateCode({
            shipmentId: selectedShipment._id,
            code: data.securityCode,
            codeType: deliverySecurity,
          }).unwrap();
        } catch (error) {
          toast.error(error?.data?.error || `Invalid ${deliverySecurity}!`);
          return;
        }
        securityDetails.value = data.securityCode;
      } else if (deliverySecurity === "ID") {
        if (!data.securityAttachments?.length) {
          toast.error("ID verification document is required.");
          return;
        }
      }

      const deliveryData = {
        ...data,
        account: data?.account?._id,
        entity: data?.entity?._id,
        sendOutId: initialState?.sendOutId?._id || "",
        shipmentId: data?.shipmentId?.map((s) => s._id),
        deliveryLocation: data?.deliveryLocation,
        securityDetails,
        fulfillmentCompletionId:
          data.fulfillmentCompletionId || `FC_${timestamp}`,
      };

      const [existingAttachments, newAttachments] = data?.attachments?.reduce(
        ([pass, fail], el) =>
          el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
        [[], []]
      );

      const [existingSecurityAttachments, newSecurityAttachments] =
        data?.securityAttachments?.reduce(
          ([pass, fail], el) =>
            el?.publicId || el?.url || el?.secureUrl
              ? [[...pass, el], fail]
              : [pass, [...fail, el]],
          [[], []]
        );

      const uploadPromises = [];

      uploadPromises.push(
        handleUploadFiles([signatureFile], {
          folderName: "DO_FOLDER_NAME.DELIVERY_SIGNATURE",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        })
      );

      if (newAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newAttachments, {
            folderName: "SHIPMENT_COMPLETION",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      if (newSecurityAttachments?.length) {
        uploadPromises.push(
          handleUploadFiles(newSecurityAttachments, {
            folderName: "DELIVERY_SECURITY_ID",
            entity: viewEntity?.entity?._id,
            account: viewEntity?.entity?.account?._id,
          })
        );
      }

      const [signatureUpload, attachmentsUpload, securityAttachmentsUpload] =
        await Promise.all(uploadPromises);

      deliveryData.signature = signatureUpload.uploadedFiles[0]?.url || "";
      deliveryData.attachments = [
        ...existingAttachments,
        ...(attachmentsUpload?.uploadedFiles || []),
      ].map((attachment) => ({
        name: attachment.name || "",
        type: attachment.type || "",
        size: attachment.size || "",
        publicId: attachment.publicId || "",
        url: attachment.secureUrl || attachment.url || "",
      }));

      let shipmentUpdateData = null;
      if (deliverySecurity === "ID") {
        shipmentUpdateData = {
          shipmentId: selectedShipment._id,
          uploadedId: [
            ...existingSecurityAttachments,
            ...(securityAttachmentsUpload?.uploadedFiles || []),
          ].map((attachment) => ({
            name: attachment.name || "",
            type: attachment.type || "",
            size: attachment.size || "",
            publicId: attachment.publicId || "",
            url: attachment.secureUrl || attachment.url || "",
          })),
        };
      }

      const { _id } = await addDelivery({ deliveryData, shipmentUpdateData }).unwrap();
      toast.success("Delivery successfully created!");
      navigate(`/app/driver/delivery/${_id}/detail`);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error((error?.data?.message ?? error.message) || "Failed to create delivery.");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let timer;
    if (isSecurityCodeDisabled && securityCodeTimer > 0) {
      timer = setInterval(() => {
        setSecurityCodeTimer((prev) => {
          if (prev <= 1) {
            setIsSecurityCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isSecurityCodeDisabled, securityCodeTimer]);

  useEffect(() => {
    let timer;
    if (isOTPCodeDisabled && otpCodeTimer > 0) {
      timer = setInterval(() => {
        setOtpCodeTimer((prev) => {
          if (prev <= 1) {
            setIsOTPCodeDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOTPCodeDisabled, otpCodeTimer]);

  // Add this useEffect to listen for payment success notifications
  // In your DeliveryFormDriver component, update the useEffect:
  useEffect(() => {
    if (selectedShipment?._id && loggedInUser?._id) {
      const socket = io();

      const eventName = `notifications/${loggedInUser._id}`;

      socket.on(eventName, (notification) => {
        if (
          notification.type === "MODEL_SPECIFIC" &&
          notification.sourceModel === "Shipment" &&
          notification.contextData?.shipmentId === selectedShipment._id &&
          notification.contextData?.notificationSubType === "PAYMENT_SUCCESS"
        ) {
          setShowPaymentSuccess(true);
          setPaymentCompleted(true);
          fonepayRef.current?.handleClosQR();
          toast.success("Payment completed successfully!");
        }
      });

      return () => {
        socket.off(eventName);
        socket.disconnect();
      };
    }
  }, [selectedShipment?._id, loggedInUser?._id]);

  const handleGenerateOTPCode = async () => {
    if (!selectedShipment || isOTPCodeDisabled) return;
    try {
      setIsOTPCodeDisabled(true);
      setOtpCodeTimer(30);
      await generateOTPCode(selectedShipment._id).unwrap();
      toast.success("OTP Code Sent to Mail!");
    } catch (error) {
      setIsOTPCodeDisabled(false);
      setOtpCodeTimer(0);
      toast.error(
        error?.data?.message ?? error?.message ?? "Couldn't generate OTP Code!"
      );
    }
  };

  const handleGenerateSecurityCode = async () => {
    if (!selectedShipment || isSecurityCodeDisabled) return;
    try {
      setIsSecurityCodeDisabled(true);
      setSecurityCodeTimer(30);
      await generateSecurityCode(selectedShipment._id).unwrap();
      toast.success("Security Code Sent to Mail!");
    } catch (error) {
      setIsSecurityCodeDisabled(false);
      setSecurityCodeTimer(0);
      toast.error(
        error?.data?.message ??
        error?.message ??
        "Couldn't generate Security Code!"
      );
    }
  };

  useEffect(() => {
    if (isDriver && !deliveryDetail) {
      const getCurrentLocation = () => {
        if (!navigator.geolocation) {
          setValue("deliveryLocation.lat", null);
          setValue("deliveryLocation.lon", null);
          toast.error("Geolocation is not supported by this browser.");
          return;
        }

        navigator.permissions
          .query({ name: "geolocation" })
          .then((permissionStatus) => {
            if (permissionStatus.state === "denied") {
              setValue("deliveryLocation.lat", null);
              setValue("deliveryLocation.lon", null);
              toast.error(
                "Location access denied. Please enable location in your browser settings."
              );
              return;
            }

            navigator.geolocation.getCurrentPosition(
              (position) => {
                const currentLat = position.coords.latitude;
                const currentLon = position.coords.longitude;
                setValue("deliveryLocation.lat", currentLat);
                setValue("deliveryLocation.lon", currentLon);
              },
              (error) => {
                console.error("Failed to fetch location:", error.message);
                setValue("deliveryLocation.lat", null);
                setValue("deliveryLocation.lon", null);
                toast.error(`Failed to fetch location: ${error.message}`);
              },
              { timeout: 10000, maximumAge: 60000 }
            );
          })
          .catch((error) => {
            console.error("Error checking geolocation permission:", error);
            setValue("deliveryLocation.lat", null);
            setValue("deliveryLocation.lon", null);
            toast.error("Error accessing geolocation permissions.");
          });
      };

      getCurrentLocation();
    }
  }, [isDriver, deliveryDetail, setValue]);

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  if (loadingProviders) {
    return <div>Loading payment options...</div>;
  }

  return (
    <AppForm loading={isBusy || loadingValidateCode || isAddingDelivery}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Row 1: Receiver and Shipments */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="receiver"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Receiver"
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("shipmentId", []);
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={receivers}
                  loading={false}
                  loadingText="Loading receivers..."
                  helperText={
                    errors?.receiver?.message ||
                    (!receivers || receivers.length === 0
                      ? "No receivers available"
                      : "")
                  }
                  error={!!errors?.receiver}
                  disabled={!!deliveryDetail || !sendOutShipments?.length}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  multiple
                  fullWidth
                  label="Shipments"
                  required
                  value={value ?? []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === (value?._id || value)
                  }
                  options={sendOutShipments}
                  loading={false}
                  loadingText="Loading shipment IDs..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!!deliveryDetail || !sendOutShipments?.length}
                />
              )}
            />
          </Grid>

          {/* Shipment Details Card (Full Width) */}
          {_watchShipmentId?.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ boxShadow: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Shipment Details
                  </Typography>
                  <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                    <p className="text-base font-medium">
                      <strong>Total Payment Amount:</strong> Rs. {_watchShipmentId.reduce((total, shipment) => {
                        return total + (shipment?.remainingShipmentAmount || 0);
                      }, 0).toFixed(2)}
                    </p>
                  </div>
                  {_watchShipmentId.map((shipment, index) => {
                    const selectedShipment = shipment;
                    const senderDetails =
                      selectedShipment?.customerDetails &&
                        Object.keys(selectedShipment.customerDetails).length > 0
                        ? selectedShipment.customerDetails
                        : selectedShipment?.vendorDetails;
                    const receiverDetails = selectedShipment?.receiverDetails;

                    return (
                      <Box
                        key={selectedShipment?._id || index}
                        sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>
                                Shipment: {selectedShipment?.shipmentId || "Unknown"}
                              </strong>
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Sender
                            </Typography>
                            {senderDetails ? (
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  <strong>Shop Name:</strong> {senderDetails.shopName || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Owner Name:</strong> {senderDetails.ownerName || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Phone:</strong> {senderDetails.phone || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Address:</strong> {senderDetails.address || "N/A"}
                                </Typography>

                                <Typography variant="body2">
                                  <strong>Payment Amount:</strong> Rs. {selectedShipment?.remainingShipmentAmount?.toFixed(2) || "0.00"}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No sender details available
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              gutterBottom
                            >
                              Receiver
                            </Typography>
                            {receiverDetails ? (
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  <strong>Name:</strong>{" "}
                                  {receiverDetails?.customerUser?.name ||
                                    receiverDetails?.name ||
                                    "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Phone:</strong>{" "}
                                  {receiverDetails?.phone || "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Address:</strong>{" "}
                                  {receiverDetails?.address || "N/A"}
                                </Typography>

                              </Stack>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No receiver details available
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Row 2: Actual Delivery Time and Receive In Behalf */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="actualDeliveryTime"
              control={control}
              rules={{ required: "Actual Delivery Time is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="datetime-local"
                  label="Actual Delivery Time"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.actualDeliveryTime}
                  helperText={errors.actualDeliveryTime?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="receiveInBehalf"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          setValue("receiveBy", "");
                          setValue("receiverPhone", "");
                        }
                      }}
                    />
                  }
                  label="Receive In Behalf?"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Grid>
          {/* Row 3: Receive By and Receiver Phone (if receiveInBehalf is true) */}
          {_watchReceiveInBehalf && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="receiveBy"
                  control={control}
                  rules={{ required: "Please provide receiver Name." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Receiver Name"
                      placeholder="Enter Receiver Name"
                      error={!!errors?.receiveBy}
                      helperText={errors?.receiveBy?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="receiverPhone"
                  control={control}
                  rules={{
                    required: "Receiver Phone is required",
                    validate: (value) => {
                      const digits = value.replace(/\D/g, "");
                      if (digits.length < 7 || digits.length > 17) {
                        return "Phone number must be between 9 and 16 digits";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomCountryPhoneInput
                      placeholder="Receiver Phone Number"
                      label="Receiver Phone"
                      {...field}
                      lockedCountry={{ idd: "+977", cca2: "NP" }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          {/* Row 4: Security Code/OTP or Security Documents */}
          {selectedShipment &&
            ["Security Code", "OTP"].includes(deliverySecurity) && (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="securityCode"
                    control={control}
                    rules={{
                      required: `${deliverySecurity} is required`,
                    }}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        required
                        label={deliverySecurity}
                        {...field}
                        error={!!errors.securityCode}
                        helperText={errors.securityCode?.message}
                        disabled={!!deliveryDetail}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                  {deliverySecurity === "Security Code" && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        loadingGenerateSecurityCode ||
                        !!deliveryDetail ||
                        isSecurityCodeDisabled
                      }
                      onClick={() => {
                        if (
                          !loadingGenerateSecurityCode &&
                          !isSecurityCodeDisabled
                        ) {
                          handleGenerateSecurityCode();
                        }
                      }}
                    >
                      {loadingGenerateSecurityCode
                        ? "Generating..."
                        : isSecurityCodeDisabled
                          ? `Wait ${securityCodeTimer}s`
                          : "Generate Security Code"}
                    </Button>
                  )}
                  {deliverySecurity === "OTP" && (
                    <Button
                      variant="contained"
                      disabled={
                        loadingGenerateOTPCode ||
                        !!deliveryDetail ||
                        isOTPCodeDisabled
                      }
                      onClick={() => {
                        if (!loadingGenerateOTPCode && !isOTPCodeDisabled) {
                          handleGenerateOTPCode();
                        }
                      }}
                    >
                      {loadingGenerateOTPCode
                        ? "Generating..."
                        : isOTPCodeDisabled
                          ? `Wait ${otpCodeTimer}s`
                          : "Generate OTP Code"}
                    </Button>
                  )}
                </Grid>
              </>
            )}
          {selectedShipment && deliverySecurity === "ID" && (
            <Grid item xs={12}>
              <FormHelperText>Security Documents</FormHelperText>
              <Controller
                name="securityAttachments"
                control={control}
                rules={{
                  validate: (value) =>
                    value?.length > 0 || "ID document is required.",
                }}
                render={({ field }) => (
                  <FileDropzone
                    multiple
                    maxFiles={2}
                    files={field.value || []}
                    onFileChange={(files) => {
                      setValue("securityAttachments", files);
                      trigger("securityAttachments");
                    }}
                    error={!!errors.securityAttachments}
                    helperText={errors.securityAttachments?.message || ""}
                    showError={isSubmitted}
                    acceptedFileTypes={{
                      "image/*": [".jpeg", ".jpg", ".png"],
                      "application/pdf": [".pdf"],
                    }}
                    disabled={!!deliveryDetail}
                  />
                )}
              />
            </Grid>
          )}
          {/* Row 5: Signature and Attachments */}
          <Grid item xs={12} sm={6}>
            <FormHelperText>Delivery Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value || []}
                  onFileChange={(files) => setValue("attachments", files)}
                  disabled={!!deliveryDetail}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SignaturePadComponent
              label="Signature"
              ref={signaturePadRef}
              InputLabelProps={{ shrink: true }}
              width={640}
              height={260}
            />
            {signatureError && (
              <FormHelperText error>
                Please sign before submitting.
              </FormHelperText>
            )}
          </Grid>

          {/* Row 6: Is Active (if deliveryDetail exists) */}
          {deliveryDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}

          <Grid item md={12} className="flex flex-col md:flex-row items-start md:items-center gap-4 mt-6">
            {!paymentCompleted ? (
              <>
                {/* NCHL Payment Option - Only show if NCHL is active */}
                {isNCHLActive && (
                  <div
                    className="flex items-center gap-2 p-3 border border-gray-300 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition"
                    onClick={() => {
                      handleSubmit(() => {
                        nchlQRRef?.current?.handleGenerateQR();
                      })();
                    }}
                  >
                    <span className="text-sm font-medium">Pay via NCHL</span>
                    <img src={nchlLogo} alt="Pay via NCHL" className="w-32 h-auto" />
                  </div>
                )}

                {/* Fonepay Payment Option - Only show if Fonepay is active */}
                {isFonepayActive && (
                  <div
                    className="flex items-center gap-2 p-5 border border-gray-300 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition"
                    onClick={handleClickFonepay}
                  >
                    <span className="text-sm font-medium">Pay via Fonepay</span>
                    <img src={fonepayLogo} alt="Pay via Fonepay" className="w-32 h-auto" />
                  </div>
                )}

                {/* Show message if no payment methods are available */}
                {!isFonepayActive && !isNCHLActive && (
                  <div className="text-gray-500 text-sm p-4 border border-gray-200 rounded-xl">
                    No payment methods are currently available.
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded">
                <CheckCircle className="w-5 h-5" />
                <span>Payment Completed</span>
              </div>
            )}
          </Grid>

          {/* QR Modals - Only render if the respective payment method is active */}
          {isFonepayActive && (
            <FonepayQR
              ref={fonepayRef}
              amount={selectedShipment?.remainingShipmentAmount || 0}
              shipmentId={selectedShipment?._id}
            />
          )}

          {isNCHLActive && (
            <DriverNCHLQRPayment
              ref={nchlQRRef}
              amount={selectedShipment?.remainingShipmentAmount}
              onSuccess={async (paymentReference) => {
                handleSubmit((payload) =>
                  handleOnSubmit({ ...payload, paymentReference })
                )();
              }}
              isBusy={isBusy}
            />
          )}

          <PaymentSuccessAnimation
            show={showPaymentSuccess}
            amount={selectedShipment?.remainingShipmentAmount}
            onComplete={() => setShowPaymentSuccess(false)}
          />

        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)} disabled={paymentCompleted}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy || loadingValidateCode || isAddingDelivery || isSubmitting}
        >
          {deliveryDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>

    </AppForm>
  );
};

export default DeliveryFormDriver;